import type { DateRangePickerValue } from '@shigo-ui/ui';

import type { ReportFilter, ReportRequest } from '../reportExplorer/model';
import { ReportDimension, ReportMetric, ReportType } from '../reportExplorer/reportEnums';
import { PerformanceDrilldownType } from './model';

export const performanceKpiMetrics = [
  ReportMetric.AdImpressions,
  ReportMetric.AdRequests,
  ReportMetric.AdRequestsFilled,
  ReportMetric.Rpm,
  ReportMetric.NetworkMarginPct,
  ReportMetric.Revenue,
  ReportMetric.PubRevenue,
  ReportMetric.NetworkProfit,
  ReportMetric.Bids,
] as const;

export const performanceDetailMetrics = [
  ReportMetric.DpAdRequests,
  ReportMetric.Bids,
  ReportMetric.WonBids,
  ReportMetric.BidRatio,
  ReportMetric.AdRequests,
  ReportMetric.AdImpressions,
  ReportMetric.FilledImpressions,
  ReportMetric.AdRequestsFilled,
  ReportMetric.Revenue,
] as const;

export const stitcherDetailMetrics = [
  ReportMetric.AdRequests,
  ReportMetric.AdImpressions,
  ReportMetric.FilledImpressions,
  ReportMetric.RenderRate,
  ReportMetric.DpAdRequests,
  ReportMetric.Bids,
  ReportMetric.BidRatio,
  ReportMetric.Revenue,
  ReportMetric.Rpm,
  ReportMetric.Profit,
  ReportMetric.StichImpressions,
  ReportMetric.AdImpressionsStitchedPct,
  ReportMetric.RevenueStitched,
  ReportMetric.RpmStitched,
  ReportMetric.RenderRateStitched,
] as const;

export function buildPerformanceKpiRequest(range: DateRangePickerValue): ReportRequest {
  return reportRequest(range, performanceKpiMetrics, []);
}

export function buildTopEntityRequest(
  range: DateRangePickerValue,
  type: PerformanceDrilldownType,
  metrics: readonly ReportMetric[],
): ReportRequest {
  const publisher = type === PerformanceDrilldownType.Publishers;
  return reportRequest(
    range,
    [
      ...metrics,
      ReportMetric.Revenue,
      publisher ? ReportMetric.PublisherName : ReportMetric.BidderCode,
      ...(publisher ? [ReportMetric.PublisherId] : []),
    ],
    [publisher ? ReportDimension.Publisher : ReportDimension.BidderCode],
  );
}

export function buildCounterpartRequest(
  range: DateRangePickerValue,
  type: PerformanceDrilldownType,
  selectedId: string,
  metrics: readonly ReportMetric[],
): ReportRequest {
  const selectedPublisher = type === PerformanceDrilldownType.Publishers;
  return reportRequest(
    range,
    [
      ...metrics,
      ReportMetric.Revenue,
      selectedPublisher ? ReportMetric.BidderCode : ReportMetric.PublisherName,
      ...(selectedPublisher ? [] : [ReportMetric.PublisherId]),
    ],
    [selectedPublisher ? ReportDimension.BidderCode : ReportDimension.Publisher],
    selectedPublisher ? { publisherIds: [selectedId] } : { dpIds: [selectedId] },
  );
}

export function buildPublisherDemandDetailRequest(
  range: DateRangePickerValue,
  type: PerformanceDrilldownType,
  selectedId: string,
  counterpartId: string,
): ReportRequest {
  const publisherId = type === PerformanceDrilldownType.Publishers ? selectedId : counterpartId;
  const demandPartnerId = type === PerformanceDrilldownType.Publishers ? counterpartId : selectedId;
  return reportRequest(range, performanceDetailMetrics, [], {
    publisherIds: [publisherId],
    dpIds: [demandPartnerId],
  });
}

export function buildTopStitcherPublishersRequest(range: DateRangePickerValue): ReportRequest {
  const request = reportRequest(
    range,
    [ReportMetric.AdImpressionsStitchedPct, ReportMetric.PublisherName, ReportMetric.PublisherId],
    [ReportDimension.Publisher],
  );
  return { ...request, dimensions: [ReportDimension.Publisher] };
}

export function buildStitcherPublishersChartRequest(
  range: DateRangePickerValue,
  publisherIds: string[],
) {
  return reportRequest(
    range,
    [ReportMetric.AdImpressionsStitchedPct, ReportMetric.PublisherName, ReportMetric.PublisherId],
    [ReportDimension.Publisher],
    { publisherIds },
  );
}

export function buildStitcherPublisherRequest(range: DateRangePickerValue, publisherId: string) {
  return reportRequest(
    range,
    [...stitcherDetailMetrics, ReportMetric.PublisherName, ReportMetric.PublisherId],
    [ReportDimension.Publisher],
    { publisherIds: [publisherId] },
  );
}

export function buildStitcherPlacementsRequest(range: DateRangePickerValue, publisherId: string) {
  return reportRequest(
    range,
    [
      ReportMetric.AdImpressionsStitchedPct,
      ReportMetric.PublisherName,
      ReportMetric.Placement,
      ReportMetric.PlacementId,
    ],
    [ReportDimension.Placement],
    { publisherIds: [publisherId] },
  );
}

export function buildStitcherPlacementsChartRequest(
  range: DateRangePickerValue,
  publisherId: string,
  placementIds: string[],
) {
  return reportRequest(
    range,
    [
      ReportMetric.AdImpressionsStitchedPct,
      ReportMetric.PublisherName,
      ReportMetric.Placement,
      ReportMetric.PlacementId,
    ],
    [ReportDimension.Placement],
    { publisherIds: [publisherId], placementIds },
  );
}

export function buildStitcherPlacementDetailRequest(
  range: DateRangePickerValue,
  publisherId: string,
  placementId: string,
) {
  return reportRequest(
    range,
    [...stitcherDetailMetrics, ReportMetric.Placement, ReportMetric.PlacementId],
    [ReportDimension.Placement],
    { publisherIds: [publisherId], placementIds: [placementId] },
  );
}

function reportRequest(
  range: DateRangePickerValue,
  metrics: readonly ReportMetric[],
  entityDimensions: readonly ReportDimension[],
  filters: Partial<ReportFilter> = {},
): ReportRequest {
  const timeDimensions =
    range.dateFrom === range.dateTo
      ? [ReportDimension.Day, ReportDimension.Hour]
      : [ReportDimension.Day];
  const timeMetrics =
    range.dateFrom === range.dateTo ? [ReportMetric.Hour, ReportMetric.Day] : [ReportMetric.Day];
  return {
    metrics: [...new Set([...metrics, ...timeMetrics])],
    dimensions: [...entityDimensions, ...timeDimensions],
    filters: {
      dateRange: null,
      dateFrom: range.dateFrom,
      dateTo: range.dateTo,
      exchangeNetworkOnly: false,
      ...filters,
    },
    source: ReportType.Exchange,
  };
}
